import React, { useEffect, useRef, useState } from "react";

import { Dialog } from "@headlessui/react";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../../../ui/Button";

interface ProductsTableComponentProps {
  getProductsList: (search: string) => Promise<any>;
  productsList: any[];
  setItemValues: (product: any) => void;
  setOpen: (open: any) => void;
}

export default function ProductsTableComponent(
  props: ProductsTableComponentProps,
) {
  const { getProductsList, productsList, setItemValues, setOpen } = props;

  const searchRef = useRef<HTMLInputElement>(null);

  const [products, setProducts] = useState<any[]>([]);

  useEffect(() => {
    setProducts(productsList);
  }, [productsList]);

  const handleSearch = async function () {
    let callResult = await getProductsList(searchRef.current?.value ?? "");
    if (callResult.success) {
      setProducts(callResult.data.data);
    }
  };

  return (
    <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6">
        <div className="px-4 sm:px-6">
          <div className="flex items-start justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              Choose a product to manufacture
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Close product selector"
                onClick={() => setOpen({ childComponent: null, open: false })}
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </Button>
            </div>
          </div>
          <div className="mt-2 flex rounded-md">
            <div className="relative flex items-stretch flex-grow focus-within:z-10">
              <input
                type="search"
                name="searchInput"
                id="searchInput"
                className="block w-full rounded-lg rounded-l-md pl-2 sm:text-sm border border-gray-300 rounded-tr-none rounded-br-none"
                placeholder="Search by name"
                ref={searchRef}
                onKeyDown={(e) => (e.key === "Enter" ? handleSearch() : null)}
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              size="md"
              className="-ml-px rounded-l-none"
              onClick={() => handleSearch()}
            >
              <MagnifyingGlassIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </Button>
          </div>
        </div>
        <div className="relative mt-6 flex-1 px-4 sm:px-6">
          <div id="product-table" className="overflow-hidden card-custom">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-white">
                <tr>
                  <th
                    scope="col"
                    className="py-3 pl-3 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Plu
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {products.length > 0 ? (
                  products.map((product) => (
                    <tr
                      key={product.id}
                      className="cursor-pointer"
                      onClick={() => {
                        setItemValues(product);
                        setOpen({ childComponent: null, open: false });
                      }}
                    >
                      <td className="whitespace-nowrap py-1.5 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                        {product.name_en}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {product.plu}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2} className="text-center p-2 text-sm">
                      No items found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
